import React, { FC, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import CustomButton from "../../components/UI/Buttons/CustomButton";
import TextInput from "../../components/UI/Inputs/TextInput";
import {
  TCheckTokenToResetFormValues,
  TResetPasswordValues,
} from "../../models/form";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../store";
import {
  checkTokenToUpdatePasswordThunk,
  updatePasswordThunk,
} from "../../store/auth/thunks";
import { NON_AUTH_ROUTE } from "../App/routes";
import { isFulfilled } from "@reduxjs/toolkit";
import { createValidationSchema } from "../../utils/createValidationSchema";
import {
  hideTransparentPreloaderAction,
  showTransparentPreloaderAction,
} from "../../store/application/slice";
import ErrorMessage from "../../components/UI/Tooltips/ErrorMessage";

interface ResetPasswordProps {}

const ResetPassword: FC<ResetPasswordProps> = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const [email, setEmail] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  // Flag to check if token validation has been completed
  const [tokenChecked, setTokenChecked] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);

  const inProgress = useSelector((state) => state.auth.inProgress);
  const error = useSelector((state) => state.auth.error);

  useEffect(() => {
    const urlSearchString = window.location.search;
    const params = new URLSearchParams(urlSearchString);
    const emailParam = params.get("email");
    const tokenParam = params.get("token");

    setEmail(emailParam);
    setToken(tokenParam);

    const checkToken = async () => {
      const checkValidTokenData: TCheckTokenToResetFormValues = {
        token: tokenParam as string,
        email: emailParam as string,
      };
      await dispatch(checkTokenToUpdatePasswordThunk(checkValidTokenData));
      // Set flag to true so that redirect logic can use it safely
      setTokenChecked(true);
      // if (!isFulfilled(statusToken as any)) navigate(NON_AUTH_ROUTE.LOGIN)
    };

    checkToken();
  }, [dispatch]);

  // If the token has been checked and there's an error, redirect to the forgot-password page
  useEffect(() => {
    if (
      tokenChecked &&
      error &&
      error.message !== "" &&
      location.pathname.includes("reset-password")
    ) {
      navigate(NON_AUTH_ROUTE.FORGOT_PASSWORD);
    }
  }, [navigate, error, location, tokenChecked]);

  // submit the form for login
  const { handleSubmit, errors, touched, handleChange } = useFormik({
    validationSchema: createValidationSchema("resetPassword"),
    initialValues: {
      email: "",
      token: "",
      password: "",
      confirm_password: "",
    },
    onSubmit: async (values: TResetPasswordValues) => {
      values.email = email as string;
      values.token = token as string;
      const payload = { ...values };
      dispatch(showTransparentPreloaderAction());
      try {
        const result = await dispatch(updatePasswordThunk(payload));
        if (isFulfilled(result)) {
          // setShowSuccessMessage(true);
          localStorage.clear();
          navigate(NON_AUTH_ROUTE.ACCOUNT_ACTIVATION); // Delay to let user read the message
        }
      } catch (error) {
        throw new Error(
          "Could not connect to services at the moment. Please try again. Thanks."
        );
      }
      dispatch(hideTransparentPreloaderAction());
    },
  });

  return (
    <>
      <div
        data-testid="reset-password"
        className="fs-22 fw-400 text-black mt-20"
      >
        {location.pathname.includes("set-password")
          ? "Set Password"
          : "Reset Password"}
      </div>
      <form
        className="d-flex flex-column gap-20"
        onSubmit={handleSubmit} // Binds Formik's submit handler to the form
      >
        <div className="d-flex flex-column gap-15">
          <TextInput
            type="password"
            field={"password"}
            label={"Password"}
            onChange={handleChange}
            touched={touched.password!}
            error={errors.password}
            placeholder=" "
          />
          <TextInput
            type="password"
            field={"confirm_password"}
            label={"Confirm Password"}
            onChange={handleChange}
            touched={touched.confirm_password!}
            error={errors.confirm_password}
            placeholder=" "
          />
        </div>
        <div className="d-flex flex-column gap-20 align-items-center">
          <CustomButton
            isLoading={inProgress}
            contentClass={"d-flex"}
            type="submit"
            variant="primary"
            className="fw-600 btn-theme bg-teal text-black min-w-250px "
          >
            Submit
          </CustomButton>
        </div>
        {error && <ErrorMessage message={error.message} />}
        {/* {showSuccessMessage && (
          <div
            className="d-flex flex-column align-items-center"
            style={{ color: "#34ada3" }}
          >
            {location.pathname.includes("set-password")
              ? "Your password has been set successfully! Redirecting to login..."
              : "Your password has been reset successfully! Redirecting to login..."}
          </div>
        )} */}
      </form>
    </>
  );
};

export default ResetPassword;
